import React from 'react';
import { Button, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './custom-markers-table.css';
import {
  CustomMarker,
  CustomMarkerChartType,
  CustomMarkerRange,
  CustomMarkerResultType,
  CUSTOM_MARKER_RESULT_SHAPE_OPTIONS,
  CUSTOM_MARKER_TEXT_MAX_LENGTH,
  CUSTOM_MARKER_EXPECTED_MAX_LENGTH,
  isTextResult,
  resultShapeValue,
  resultShapeFields,
  CUSTOM_MARKER_DEFAULT_PRECISION,
  CUSTOM_MARKER_RANGE_KEYS,
  CUSTOM_MARKER_SECTION_OPTIONS,
  CUSTOM_MARKER_OWN_SECTION_LABEL,
  CustomMarkerSection,
  assignableSection,
  practitionerSectionFromOptionValue,
  practitionerSectionKey,
  practitionerSectionOptionValue,
  practitionerSectionProblem,
  sectionOptionValueFor,
  shippedMarkerNamed,
  MARKER_NAME_PLACEHOLDER_EXAMPLE,
  MARKER_UNITS_PLACEHOLDER_EXAMPLE,
  MARKER_VALUE_PLACEHOLDER_EXAMPLE,
  MARKER_NAME_PLACEHOLDER_TEXT_EXAMPLE,
  CustomMarkerRangeKey,
  CustomMarkerDefinition,
  CustomMarkerSectionGroup,
  customMarkerFromDefinition,
  groupBySectionValue,
  groupDefinitionsBySection,
  isExpandedTestSection,
  sectionGroupKey,
  PanelField,
} from '../../models';
import { MathUtils } from '../../utils/MathUtils';

/**
 * A row is held as RAW STRINGS while the practitioner types, not as numbers:
 *
 *  - a half-typed "4." or "-" must survive a re-render;
 *  - the number of decimal places TYPED is what drives the marker's precision
 *    ("4.0" is 1 decimal place, but Number('4.0') is 4 and tells us nothing).
 *
 * customMarkersFromRows() does the conversion at save time.
 */
export type CustomMarkerRow = {
  name: string;
  units: string;
  /**
   * The result, whichever kind it is.
   *
   * One cell rather than two because the practitioner types into ONE box either
   * way, and the row already holds everything as raw strings. `resultType` says
   * how to read it at save time; keeping the typed characters in one place is
   * also what lets a row switched from Text to a chart shape keep what was in
   * the box.
   */
  value: string;
  /** Numeric or text — see CustomMarkerResultType. */
  resultType: CustomMarkerResultType;
  /** Optional expected wording for a text row. Ignored on a numeric one. */
  expectedText: string;
  chartType: CustomMarkerChartType;
  /**
   * The report section to chart this marker in, or '' for its own
   * Other Lab Results section. Held as a plain string like every other
   * cell; validated back to a real Section by assignableSection() at save.
   *
   * A section the practitioner named themselves is carried here too, prefixed
   * (see practitionerSectionOptionValue) so one <select> can offer both without
   * a name ever being mistaken for a shipped section.
   */
  section: string;
  alarmMin: string;
  labMin: string;
  optimalMin: string;
  optimalMax: string;
  labMax: string;
  alarmMax: string;
  /**
   * This row's definition was copied from the client's previous test so the
   * practitioner only has to type the value. UI-only — never persisted.
   *
   * A carried-forward row left without a value means "that marker was not on
   * this panel", so it is dropped at save instead of failing validation. A row
   * typed by hand still requires its value.
   */
  carriedForward?: boolean;
  /**
   * This row was picked deliberately from the My Markers picker, rather than
   * pre-filled by LabSmarts. UI-only — never persisted.
   *
   * Both kinds are carriedForward (neither obliges a value), so without this
   * they were indistinguishable, and rebuilding the table when a client was
   * chosen threw away markers the practitioner had just gone and asked for.
   */
  pickedByHand?: boolean;
};

export function emptyCustomMarkerRow(): CustomMarkerRow {
  return {
    name: '',
    units: '',
    value: '',
    resultType: CustomMarkerResultType.NUMERIC,
    expectedText: '',
    chartType: CustomMarkerChartType.STANDARD,
    section: '',
    alarmMin: '',
    labMin: '',
    optimalMin: '',
    optimalMax: '',
    labMax: '',
    alarmMax: '',
  };
}

const asText = (value: any): string =>
  value === undefined || value === null ? '' : String(value);

export function rowsFromCustomMarkers(
  customMarkers?: CustomMarker[],
): CustomMarkerRow[] {
  if (!Array.isArray(customMarkers) || !customMarkers.length) {
    return [emptyCustomMarkerRow()];
  }
  return customMarkers.map(customMarker => ({
    name: asText(customMarker.name),
    units: asText(customMarker.units),
    // A text marker keeps its result in textValue; both land in the one box.
    value: isTextResult(customMarker)
      ? asText(customMarker.textValue)
      : asText(customMarker.value),
    resultType: isTextResult(customMarker)
      ? CustomMarkerResultType.TEXT
      : CustomMarkerResultType.NUMERIC,
    expectedText: asText(customMarker.expectedText),
    chartType: customMarker.chartType || CustomMarkerChartType.STANDARD,
    // Anything no longer assignable reads as blank, so the dropdown shows the
    // fallback the report is actually using rather than a dead value. A
    // practitioner section stays selected even if it has since been deleted
    // from My Markers — the marker charts under it either way, so the dropdown
    // has to admit that is where it is.
    section: sectionOptionValueFor(customMarker),
    alarmMin: asText(customMarker.alarmMin),
    labMin: asText(customMarker.labMin),
    optimalMin: asText(customMarker.optimalMin),
    optimalMax: asText(customMarker.optimalMax),
    labMax: asText(customMarker.labMax),
    alarmMax: asText(customMarker.alarmMax),
  }));
}

/**
 * Rows for the client's existing custom markers, definitions intact and values
 * blank, so a repeat test only needs the numbers typed in.
 *
 * Ranges are COPIED onto the new test rather than referenced, keeping custom
 * markers per-test: revising a range here can never rewrite history on the test
 * they came from.
 */
export function carryForwardRowsFrom(
  customMarkers: CustomMarker[],
): CustomMarkerRow[] {
  // Nothing carried forward is NO rows, not one blank one. rowsFromCustomMarkers
  // answers an empty list with the table's blank starter row, which is right
  // for loading a test and wrong here: stamped carriedForward it folded itself
  // to the compact layout — no Marker Name box, no Units box, and the table
  // dropping every column its folded rows do not fill — so a practitioner with
  // no history and no default sections was handed a row with nowhere to type.
  if (!Array.isArray(customMarkers) || !customMarkers.length) {
    return [];
  }
  return rowsFromCustomMarkers(customMarkers).map(row => ({
    ...row,
    value: '',
    carriedForward: true,
  }));
}

/**
 * Whether the practitioner has actually put something in this table, as
 * opposed to LabSmarts having pre-filled it for them.
 *
 * "Is any row non-blank" used to answer this, and stopped being able to the
 * moment default sections started loading before a client is chosen: those rows
 * carry a name, units and a range from the second the screen opens, so a
 * non-blank test would report input on a table nobody had touched.
 *
 * Input is a result typed into any row, or any content at all in a row that was
 * not pre-filled. A pre-filled row still waiting for its value is not input,
 * which is what lets a client being picked rebuild the table for their sex.
 */
export function hasPractitionerInput(rows: CustomMarkerRow[]): boolean {
  return (rows || []).some(
    row =>
      (row.value || '').trim() ||
      row.pickedByHand ||
      (!row.carriedForward && !isBlankCustomMarkerRow(row)),
  );
}

/** The rows the practitioner picked from My Markers themselves. */
export function pickedByHandRows(rows: CustomMarkerRow[]): CustomMarkerRow[] {
  return (rows || []).filter(row => row.pickedByHand);
}

/** A carried-forward marker the practitioner never filled in — not on this panel. */
export function isUnusedCarriedForwardRow(row: CustomMarkerRow): boolean {
  return !!row.carriedForward && !row.value.trim();
}

/**
 * A row built from a My Markers definition, resolved for this client's sex.
 *
 * Flagged carriedForward for the same reason a genuinely carried-forward row
 * is: the practitioner asked for the definition, not for an obligation to fill
 * it in, so picking one and changing their mind costs nothing.
 */
export function rowFromDefinition(
  definition: CustomMarkerDefinition,
  gender?: string,
): CustomMarkerRow {
  const [row] = carryForwardRowsFrom([
    customMarkerFromDefinition(definition, gender),
  ]);
  return row;
}

/** Marker-name keys already in the table, for disabling them in the picker. */
export function rowKeysInUse(rows: CustomMarkerRow[]): Set<string> {
  const keys = new Set<string>();
  (rows || []).forEach(row => {
    const name = (row.name || '').trim();
    if (name) {
      keys.add(CustomMarker.keyFor(name));
    }
  });
  return keys;
}

/**
 * Append library definitions to the table, skipping any marker already on it.
 *
 * The one path by which a definition becomes a row, whether it was picked one
 * at a time, added a whole section at a time, or loaded because the section is
 * one of the practitioner's defaults. Keeping it in one function is what makes
 * those three land identically: same ranges resolved for the same client, same
 * dedupe, same reuse of the blank starter row.
 *
 * Returns the rows unchanged when there is nothing to add, so a caller can use
 * identity to decide whether anything happened.
 */
export function addDefinitionsToRows(
  rows: CustomMarkerRow[],
  definitions: CustomMarkerDefinition[] | undefined,
  clientGender?: string,
  options: { byHand?: boolean } = {},
): CustomMarkerRow[] {
  const inUse = rowKeysInUse(rows);
  const additions: CustomMarkerRow[] = [];
  (definitions || []).forEach(definition => {
    if (!definition || !(definition.name || '').trim()) {
      return;
    }
    const key = CustomMarker.keyFor(definition.name);
    if (inUse.has(key)) {
      return;
    }
    // Marked in use as we go, so a library holding the same name twice cannot
    // put two rows with one name on the test.
    inUse.add(key);
    const added = rowFromDefinition(definition, clientGender);
    if (options.byHand) {
      added.pickedByHand = true;
    }
    additions.push(added);
  });
  if (!additions.length) {
    return rows;
  }
  // The single blank starter row is reused rather than left stranded above the
  // pick.
  const keep = (rows || []).filter(row => !isBlankCustomMarkerRow(row));
  return [...keep, ...additions];
}

/**
 * Move one row into another's place, WITHIN the slots its section already
 * occupies in the rows array.
 *
 * Order matters because it IS report order: BloodworkAnalysis reads
 * bloodwork.customMarkers in array order and never sorts it, so a row moved
 * here moves on the analysis screen and in the PDF. Nothing about the order was
 * choosable before this — it was the order the rows happened to be typed in.
 *
 * The array holds every section's markers and a section's rows are NOT
 * necessarily adjacent in it (a row's section is a dropdown, changed at any
 * time). Splicing the whole array would therefore drag other sections' rows
 * around underneath. So the section's global positions are treated as fixed
 * slots, its own rows are permuted between them, and every other row stays
 * exactly where it was.
 *
 * Returns the SAME array when nothing moved, so callers can skip the update.
 *
 * Generic because My Markers reorders its definitions by exactly these rules,
 * grouped by exactly the same helper — two copies of this would be two places
 * for the slot arithmetic to be got subtly wrong.
 */
export function moveRowWithin<T>(
  rows: T[],
  slots: number[],
  fromIndex: number,
  toIndex: number,
): T[] {
  const ordered = [...slots].sort((a, b) => a - b);
  const from = ordered.indexOf(fromIndex);
  const to = ordered.indexOf(toIndex);
  // A drag that ended on another section's row lands here with from or to
  // missing, and is a no-op: a marker changes section through the Section
  // dropdown, which is the control that also re-files it on the report.
  if (from < 0 || to < 0 || from === to) {
    return rows;
  }
  const members = ordered.map(slot => rows[slot]);
  const [moved] = members.splice(from, 1);
  members.splice(to, 0, moved);
  const next = [...rows];
  ordered.forEach((slot, i) => {
    next[slot] = members[i];
  });
  return next;
}

export function isBlankCustomMarkerRow(row: CustomMarkerRow): boolean {
  if (row.name.trim() || row.value.trim()) {
    return false;
  }
  // The fields this row is SHOWING, for the reason isBlankDefinitionRow gives:
  // a value held by an input that is not rendered leaves a row looking empty
  // and refusing to validate, with nothing on screen to clear.
  return isTextResult(row)
    ? !(row.expectedText || '').trim()
    : !row.units.trim() &&
        CUSTOM_MARKER_RANGE_KEYS.every(key => !row[key].trim());
}

/**
 * The hint in a row's Value box, or undefined for no hint.
 *
 * A numeric row only gets one while it is still unnamed, so that the boxes read
 * across as one worked example (Lipase, 50, U/L). The moment the marker is
 * named — typed, or picked from My Markers — a lipase result suggested beside
 * "Zinc, RBC" is an example of the wrong thing, which is the same rule
 * MARKER_UNITS_PLACEHOLDER_EXAMPLE is written under. A text row keeps its own
 * hint either way: it is showing the SHAPE of a result reported in words, which
 * holds whatever the marker is called.
 */
export function valuePlaceholder(row: CustomMarkerRow): string | undefined {
  if (isTextResult(row)) {
    return 'e.g. Negative';
  }
  return row.name.trim()
    ? undefined
    : `e.g. ${MARKER_VALUE_PLACEHOLDER_EXAMPLE}`;
}

const toNumber = (text: string): number | undefined => {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return isNaN(parsed) ? undefined : parsed;
};

/** Decimal places in a typed number, e.g. "4.05" -> 2, "9" -> 0, "1e3" -> 0. */
const decimalsIn = (text: string): number => {
  const trimmed = (text || '').trim();
  const dot = trimmed.indexOf('.');
  if (dot < 0) {
    return 0;
  }
  return trimmed.slice(dot + 1).replace(/[^0-9]/g, '').length;
};

/**
 * Precision for a row: the most decimal places the practitioner typed across
 * the value and the two lab bounds, clamped to 0..3.
 *
 * Must never come back undefined — MathUtils.round(x, undefined) is NaN, and
 * precision is read by PanelMarker.displayValue(), ReferenceRangeChart and
 * PanelRange.init().
 */
export function precisionForRow(row: CustomMarkerRow): number {
  const typed = [row.value, row.labMin, row.labMax].filter(t =>
    (t || '').trim(),
  );
  if (!typed.length) {
    return CUSTOM_MARKER_DEFAULT_PRECISION;
  }
  return Math.min(3, Math.max(0, ...typed.map(decimalsIn)));
}

export function customMarkersFromRows(
  rows: CustomMarkerRow[],
): CustomMarker[] {
  return (rows || [])
    .filter(row => !isBlankCustomMarkerRow(row))
    // A marker carried forward from the previous test but not filled in was not
    // run on this panel, so it does not belong on this record.
    .filter(row => !isUnusedCarriedForwardRow(row))
    .map(row => {
      const isText = isTextResult(row);
      const customMarker = isText
        ? // Nothing about a chart, a range or a precision applies, and writing
          // them anyway would leave a text marker carrying a lab range it can
          // never be charted against.
          new CustomMarker({
            name: row.name.trim(),
            resultType: CustomMarkerResultType.TEXT,
            textValue: row.value.trim(),
          })
        : new CustomMarker({
            name: row.name.trim(),
            chartType: row.chartType || CustomMarkerChartType.STANDARD,
            precision: precisionForRow(row),
            value: toNumber(row.value),
          });
      // Left off entirely when the marker keeps its own section, so the
      // attribute never lands on the DynamoDB item as an empty string.
      const section = assignableSection(row.section);
      if (section) {
        customMarker.section = section;
      } else {
        // The NAME is stored, not a reference to the profile's section list:
        // renaming or deleting the section later must not retitle a section on
        // a report already issued to a client.
        const practitionerSection = practitionerSectionFromOptionValue(
          row.section,
        );
        if (practitionerSection) {
          customMarker.practitionerSection = practitionerSection;
        }
      }
      // Empty strings are deliberately never persisted — DynamoDB rejects them
      // and the rest of this form strips them the same way.
      if (isText) {
        const expected = (row.expectedText || '').trim();
        if (expected) {
          customMarker.expectedText = expected;
        }
        return customMarker;
      }
      const units = row.units.trim();
      if (units) {
        customMarker.units = units;
      }
      CUSTOM_MARKER_RANGE_KEYS.forEach(key => {
        const parsed = toNumber(row[key]);
        if (parsed !== undefined) {
          customMarker[key] = parsed;
        }
      });
      return customMarker;
    });
}

/**
 * Per-row validation. Returns a Map keyed the same way the rest of the form
 * keys its errors, so the messages flow into renderValidationErrors() unchanged.
 * Messages are self-contained (they name the row) because findFieldLabel()
 * resolves nothing for these synthetic keys.
 */
export function validateCustomMarkerRows(
  rows: CustomMarkerRow[],
): Map<string, string> {
  const errors = new Map<string, string>();
  const seenNames = new Map<string, number>();

  (rows || []).forEach((row, index) => {
    if (isBlankCustomMarkerRow(row)) {
      return;
    }
    // Carried forward and left alone: that marker simply was not on this
    // panel. It gets dropped at save, so there is nothing to complain about.
    if (isUnusedCarriedForwardRow(row)) {
      return;
    }

    const rowLabel = row.name.trim()
      ? `Custom marker "${row.name.trim()}":`
      : `Custom marker row ${index + 1}:`;
    const fail = (suffix: string, message: string) =>
      errors.set(`customMarker${index}${suffix}`, `${rowLabel} ${message}`);

    const name = row.name.trim();
    if (!name) {
      fail('name', 'enter a marker name, or clear the row.');
      return;
    }

    if (!row.value.trim()) {
      fail('value', 'enter the result for this marker, or clear the row.');
      return;
    }

    // A text result is words, so there are no units to report and no range to
    // check. Only the name, the result and the no-duplicates rule apply.
    if (isTextResult(row)) {
      if (row.value.trim().length > CUSTOM_MARKER_TEXT_MAX_LENGTH) {
        fail(
          'textLength',
          `the result is limited to ${CUSTOM_MARKER_TEXT_MAX_LENGTH} characters. Put anything longer in the section's Notes.`,
        );
        return;
      }
      const nameKeyForText = CustomMarker.keyFor(name);
      if (seenNames.has(nameKeyForText)) {
        fail(
          'duplicate',
          `this name is already used on row ${seenNames.get(nameKeyForText) + 1} of this test. Use a different name.`,
        );
        return;
      }
      seenNames.set(nameKeyForText, index);
      return;
    }

    const nameKey = CustomMarker.keyFor(name);
    if (seenNames.has(nameKey)) {
      fail(
        'duplicate',
        `this name is already used on row ${seenNames.get(nameKey) + 1} of this test. Use a different name.`,
      );
      return;
    }
    seenNames.set(nameKey, index);

    // Every filled range cell must parse.
    let unparseable = false;
    ['value', ...CUSTOM_MARKER_RANGE_KEYS].forEach(key => {
      const text = (row[key] || '').trim();
      if (text && toNumber(text) === undefined) {
        unparseable = true;
      }
    });
    if (unparseable) {
      fail('numeric', 'every value and range entered must be numeric.');
      return;
    }

    const rangeProblem = validateCustomMarkerRangeStrings(
      row,
      row.chartType,
      precisionForRow(row),
    );
    if (rangeProblem) {
      fail(rangeProblem.key, rangeProblem.message);
    }
  });

  return errors;
}

/** Just the six boundary cells of a row, as typed. */
export type CustomMarkerRangeStrings = {
  [K in CustomMarkerRangeKey]: string;
};

/**
 * The value LabSmarts will use for a boundary the practitioner left blank, as
 * a display string — or '' when there is not enough entered to derive one.
 *
 * Computed through the very same CustomMarkerRange the report charts with, so
 * the preview can never drift from what actually gets used. Shared by the
 * per-test table and the My Markers library.
 */
export function derivedRangeValue(
  ranges: CustomMarkerRangeStrings,
  chartType: CustomMarkerChartType,
  precision: number,
  key: CustomMarkerRangeKey,
): string {
  const marker = new CustomMarker({
    // isRenderable() requires a name; nothing else here reads it.
    name: 'preview',
    chartType: chartType || CustomMarkerChartType.STANDARD,
    precision,
  });
  CUSTOM_MARKER_RANGE_KEYS.forEach(rangeKey => {
    const parsed = toNumber(ranges[rangeKey]);
    if (parsed !== undefined) {
      marker[rangeKey] = parsed;
    }
  });
  if (!marker.isRenderable()) {
    return '';
  }

  const labMin = toNumber(ranges.labMin);
  const labMax = toNumber(ranges.labMax);
  if (labMax !== undefined && labMin !== undefined && labMax <= labMin) {
    return '';
  }

  const derived = new CustomMarkerRange(marker).init();
  const value = derived[key];
  return value == null ? '' : String(value);
}

/**
 * Whether a boundary is one LabSmarts calculates when left blank. Lab Min never
 * is; Lab Max only for an open-ended "≥ N" range, where there is none to enter.
 */
export function isCalculatedRangeKey(
  key: CustomMarkerRangeKey,
  chartType: CustomMarkerChartType,
): boolean {
  if (key === 'labMin') {
    return false;
  }
  if (key === 'labMax') {
    return chartType === CustomMarkerChartType.HIGHER_IS_BETTER;
  }
  return true;
}

/**
 * The reference-range rules, independent of who is entering them — shared by
 * the per-test table and the My Markers library page so the two can
 * never drift. Returns the first problem found, or undefined when the ranges
 * are usable.
 *
 * Assumes every filled cell already parses as a number; callers check that
 * first so they can word the message for their own context.
 */
export function validateCustomMarkerRangeStrings(
  ranges: CustomMarkerRangeStrings,
  chartType: CustomMarkerChartType,
  precision: number,
): { key: string; message: string } | undefined {
  const labMin = toNumber(ranges.labMin);
  const labMax = toNumber(ranges.labMax);
  // A one-sided "≥ N" range charted as "only low is a concern" has no lab max
  // to give, so the width is derived from the cut-off alone — see
  // CustomMarkerRange.init().
  const isOpenEndedHigh =
    chartType === CustomMarkerChartType.HIGHER_IS_BETTER &&
    labMax === undefined;

  if (labMin === undefined) {
    return { key: 'lab', message: 'lab min is required.' };
  }
  if (labMin < 0) {
    return { key: 'labMinNegative', message: 'lab min cannot be negative.' };
  }

  if (isOpenEndedHigh) {
    // The only thing a "≥ N" range has to give is N, and it has to be a real
    // number for the derived width (a quarter of it) to mean anything.
    if (labMin <= 0) {
      return {
        key: 'openEndedLabMin',
        message:
          'for a "≥" range charted as "only low is a concern", enter the lab’s cutoff as Lab Min. It must be greater than 0, or add a Lab Max.',
      };
    }
  } else {
    if (labMax === undefined) {
      return { key: 'lab', message: 'lab min and lab max are required.' };
    }
    if (labMax <= labMin) {
      return {
        key: 'labOrder',
        message: 'lab max must be greater than lab min.',
      };
    }

    // The range must still have width once rounded to the marker's precision,
    // otherwise the derived std dev is 0 and the chart divides by zero.
    if (
      MathUtils.round(labMax, precision) <= MathUtils.round(labMin, precision)
    ) {
      return {
        key: 'labSpread',
        message: `lab min and lab max are too close together to chart at ${precision} decimal place${precision === 1 ? '' : 's'}.`,
      };
    }
  }

  // Non-decreasing across whichever boundaries were entered.
  const entered: number[] = [];
  CUSTOM_MARKER_RANGE_KEYS.forEach(key => {
    const parsed = toNumber(ranges[key]);
    if (parsed !== undefined) {
      entered.push(parsed);
    }
  });
  for (let i = 1; i < entered.length; i++) {
    if (entered[i] < entered[i - 1]) {
      return {
        key: 'order',
        message:
          'ranges must be in order: alarm min ≤ lab min ≤ optimal min ≤ optimal max ≤ lab max ≤ alarm max.',
      };
    }
  }

  return undefined;
}

/**
 * The Card.Header for the custom-markers card: the title plus an info icon that
 * opens the "how this table works" popover. Lives here rather than in
 * bloodwork-entry.tsx so the explanation stays next to the code it describes.
 */
export const CustomMarkersHeader: React.FC = () => (
  <span>
    Add Your Own Markers to This Test&nbsp;&nbsp;
    <OverlayTrigger
      trigger="click"
      rootClose
      placement="bottom"
      overlay={
        <Popover id="custom-markers-help" className="custom-marker-help-popover">
          <Popover.Title as="h6">
            Adding a marker LabSmarts does not cover
          </Popover.Title>
          <Popover.Content>
            <p>
              Enter the marker exactly as the lab reported it: its name, the
              result, the units, and the lab&rsquo;s reference range. It is
              charted in its own &ldquo;{CUSTOM_MARKER_OWN_SECTION_LABEL}&rdquo;
              section and included in the analysis and comparison reports.
            </p>
            <p>
              <strong>Section</strong> files it among a body system&rsquo;s own
              bar graphs instead (Lipase under Digestive &amp; Gut Health, say),
              after that section&rsquo;s own markers, on screen, in the PDF and
              in the comparison report. From then on it{' '}
              <strong>counts in that section</strong>: it can set the
              section&rsquo;s color in Results Overview and it appears in
              Markers Outside Optimal, on the ranges you entered. It never
              changes a <strong>pattern</strong> LabSmarts detects, because
              those read only the markers LabSmarts ships.
            </p>
            <p>
              The result is saved <strong>with this test only</strong>, but the
              marker&rsquo;s <strong>definition</strong> is kept: it is also
              saved to{' '}
              <a href="/my-markers" target="_blank" rel="noopener noreferrer">
                <strong>My Markers</strong>
              </a>{' '}
              (under <strong>Test Results</strong> in the menu), so the next
              client only needs it picked from the list rather than defined
              again. Editing it there changes tests you add from then on; tests
              you have already saved keep the ranges they were interpreted
              against.
            </p>
            <p className="mb-1">
              <strong>Only Lab Min and Lab Max are required.</strong> Leave
              Optimal and Alarm blank and LabSmarts calculates them from the lab
              range, the same way it does for a shipped marker whose lab has not
              published them. Calculated cells are shaded gray and show the
              number that will be used. <strong>Type in a gray cell to set your
              own</strong> whenever the calculated bound is not the one you
              want.
            </p>
            <p className="mb-1" style={{ marginTop: '10px' }}>
              For a marker the lab reports in words, use{' '}
              <strong>+ Add a text marker</strong>. <strong>Chart Type</strong>{' '}
              has its own help icon, beside that column&rsquo;s heading.
            </p>
            <p className="mb-1" style={{ marginTop: '10px' }}>
              <strong>Drag a row by the handle at its left</strong> to set the
              order these markers are displayed in. That column has its own help
              icon too, above the handles.
            </p>
          </Popover.Content>
        </Popover>
      }
    >
      <FontAwesomeIcon
        icon="info-circle"
        className="custom-marker-help-icon"
        title="How this table works"
      />
    </OverlayTrigger>
  </span>
);

/**
 * The Chart Type explanation, on an icon beside the column it explains.
 *
 * Split out of the table's title popover because it answers a question asked at
 * one moment only: which of the four shapes THIS marker is. Folded in there it
 * was two fifths of the prose a practitioner had to read past to reach anything
 * else, and it sat nowhere near the dropdown it describes.
 *
 * `options` because the picker is not the same everywhere: the per-test table
 * and the My Markers row editor offer "Text result", the quick-add draft form
 * does not, and describing an option the dropdown below does not list is how
 * help text sends someone hunting for a control that is not there.
 */
export const ChartTypeHelp: React.FC<{
  options?: typeof CUSTOM_MARKER_RESULT_SHAPE_OPTIONS;
}> = ({ options = CUSTOM_MARKER_RESULT_SHAPE_OPTIONS }) => (
  <OverlayTrigger
    trigger="click"
    rootClose
    placement="bottom"
    overlay={
      <Popover
        id="custom-marker-chart-type-help"
        className="custom-marker-help-popover"
      >
        <Popover.Title as="h6">Chart Type</Popover.Title>
        <Popover.Content>
          <p className="mb-1">
            Which direction is a concern for this marker. That is what sets
            the colors on its bar graph:
          </p>
          <dl>
            {options.map(option => (
              <React.Fragment key={option.value}>
                <dt>{option.label}</dt>
                <dd>{option.description}</dd>
                {option.example ? (
                  <dd className="custom-marker-help-example">
                    {option.example}
                  </dd>
                ) : null}
              </React.Fragment>
            ))}
          </dl>
        </Popover.Content>
      </Popover>
    }
  >
    <FontAwesomeIcon
      icon="info-circle"
      className="custom-marker-help-icon custom-marker-help-icon-inline"
      title="How Chart Type works"
    />
  </OverlayTrigger>
);

/**
 * The help for the reorder column, at the column.
 *
 * The handle says "this row can be moved" and nothing more. What it cannot say
 * is that the order is the ORDER THE REPORT PRINTS IN, which is the only reason
 * to touch it, or that the arrow keys do the same job — and a control whose
 * point has to be read three paragraphs into another popover is a control most
 * people never use. Built exactly like ChartTypeHelp, the other column that
 * needed more explaining than its heading could hold.
 */
export const ReorderHelp: React.FC = () => (
  <OverlayTrigger
    trigger="click"
    rootClose
    placement="bottom-start"
    overlay={
      <Popover
        id="custom-marker-reorder-help"
        className="custom-marker-help-popover"
      >
        <Popover.Title as="h6">Order</Popover.Title>
        <Popover.Content>
          <p>
            The order of these rows is the order the markers are displayed, on
            the analysis screen, in the comparison table, and in their
            respective PDFs.
          </p>
          <p>
            <strong>Drag a row by its handle</strong> to move it, or select a
            handle and use the <strong>up and down arrow keys</strong>.
          </p>
          <p className="mb-1">
            Text markers always print below the bar graphs, so the order you set
            applies within each of those two groups. Moving a marker to a
            different <strong>Section</strong> is the Section dropdown&rsquo;s
            job, not a drag.
          </p>
        </Popover.Content>
      </Popover>
    }
  >
    <FontAwesomeIcon
      icon="info-circle"
      className="custom-marker-help-icon custom-marker-reorder-help-icon"
      title="How the order works"
    />
  </OverlayTrigger>
);

/** Sentinel option value that turns the Section dropdown into a name box. */
const NEW_SECTION_OPTION = '__new-section__';

/**
 * The Section dropdown lists alphabetically, not in report order.
 *
 * Report order is the right order to READ a report in and the wrong one to
 * FIND a section in: there are sixteen shipped sections plus however many the
 * practitioner has added, they are picked one marker row at a time, and nobody
 * scanning for "Thyroid Function" knows it sits between Inflammation and Stress
 * & Adrenal. Sorted on the LABEL, which is the text being scanned, and with
 * localeCompare so accented and cased names land where a reader expects.
 */
function byLabel<T extends { label: string }>(options: T[]): T[] {
  return [...options].sort((a, b) => a.label.localeCompare(b.label));
}

function byName<T extends { name: string }>(sections: T[]): T[] {
  return [...sections].sort((a, b) =>
    (a.name || '').localeCompare(b.name || ''),
  );
}

export const NEW_SECTION_OPTION_LABEL = '+ Add a new section…';

type SectionSelectProps = {
  /** '' for Other Lab Results, a Section, or a prefixed section name. */
  value: string;
  onChange: (value: string) => void;
  /** The practitioner's own sections, offered under the shipped ones. */
  sections?: CustomMarkerSection[];
  /**
   * Saves a newly typed section to the practitioner's profile. Omitted where
   * there is nowhere to save it to, which also drops the "+ Add a new section"
   * option rather than offering something that cannot work.
   */
  onCreateSection?: (name: string) => void;
  /**
   * Corrects a section's name everywhere it is in play right now: on this test
   * (every marker filed under it) and in the practitioner's section list. Given
   * a marker stores the name it was saved with, a typo would otherwise have to
   * be fixed one marker row at a time.
   */
  onRenameSection?: (from: string, to: string) => void;
  style?: React.CSSProperties;
};

/**
 * The Section dropdown, shared by the per-test table and My Markers.
 *
 * A practitioner section is created right here rather than only on the My
 * Markers page: the moment you need "IgG Allergens" is while you are typing the
 * IgG panel onto a test, and sending someone to another screen mid-entry is how
 * a half-filled test gets abandoned. Whatever is created is saved to the
 * profile, so it is on the list for every client afterwards.
 */
export const SectionSelect: React.FC<SectionSelectProps> = ({
  value,
  onChange,
  sections,
  onCreateSection,
  onRenameSection,
  style,
}) => {
  const [typing, setTyping] = React.useState<'new' | 'rename' | undefined>(
    undefined,
  );
  const [name, setName] = React.useState('');
  const [problem, setProblem] = React.useState<string | undefined>(undefined);

  // Table rows are keyed by position, so deleting a row hands this instance the
  // NEXT row's placement. Half-typed section names must not ride along with it.
  React.useEffect(() => {
    setTyping(undefined);
    setName('');
    setProblem(undefined);
  }, [value]);

  const saved = sections || [];
  // A section the marker is already filed under but which is no longer on the
  // profile (renamed or deleted since) still has to appear, or the dropdown
  // would silently show "Other Lab Results" for a marker charting
  // somewhere else entirely.
  const selectedName = practitionerSectionFromOptionValue(value);
  // Matched on the KEY, because the two spellings of one section drift apart
  // the moment it is renamed: the profile's list takes the new spelling while
  // a marker keeps the one it was filed under, and a library definition keeps
  // its own until the section is next saved from My Markers. Everything that
  // GROUPS these markers already reads through sectionGroupKey, so the card
  // headed "Cancer Tests" was showing rows whose dropdown said "Other Lab
  // Results" — the <select> was the one place still comparing the raw strings,
  // and a value matching no option renders as the first one.
  const listedAs = saved.find(
    section =>
      practitionerSectionKey(section.name) ===
      practitionerSectionKey(selectedName),
  );
  const options = byName(
    selectedName && !listedAs
      ? [...saved, new CustomMarkerSection({ id: `unsaved:${selectedName}`, name: selectedName })]
      : saved,
  );
  // The option that IS this marker's section, spelled the way the practitioner
  // spells it now. Picking anything in the dropdown writes that spelling back
  // to the row, so an edit quietly settles the drift as well.
  const selectedValue = listedAs
    ? practitionerSectionOptionValue(listedAs.name)
    : value;

  const commit = () => {
    const trimmed = name.trim();
    const renaming = typing === 'rename' && selectedName;
    // Renaming a section to itself, or to a different capitalization of itself,
    // is not a duplicate of itself.
    const others = renaming
      ? saved.filter(
          section =>
            practitionerSectionKey(section.name) !==
            practitionerSectionKey(selectedName),
        )
      : saved;
    const failure = practitionerSectionProblem(trimmed, others);
    if (failure) {
      setProblem(failure);
      return;
    }
    if (renaming) {
      if (onRenameSection) {
        onRenameSection(selectedName!, trimmed);
      }
    } else {
      if (onCreateSection) {
        onCreateSection(trimmed);
      }
      // Only a NEWLY created section is this row's own placement to set. A
      // rename moves every row filed under the old name, this one included,
      // and it does that through onRenameSection above — which hands the
      // parent a whole new rows array. Firing onChange as well wrote this
      // row's new section over a rows array captured BEFORE that move (both
      // callers map this.props.rows, and props cannot have updated yet inside
      // one event handler), so the second write won and every OTHER row went
      // back to the name that no longer exists. They then fell out of
      // expandedTestSections — which the rename does correct — and folded,
      // which is what threw the page's scroll position as well.
      onChange(practitionerSectionOptionValue(trimmed));
    }
    setTyping(undefined);
    setName('');
    setProblem(undefined);
  };

  const cancel = () => {
    setTyping(undefined);
    setName('');
    setProblem(undefined);
  };

  if (typing) {
    return (
      <div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Form.Control
            type="text"
            autoFocus
            className="custom-marker-input"
            style={{ ...(style || {}), flex: '1 1 auto' }}
            placeholder="e.g. IgG Allergens"
            value={name}
            onChange={(e: any) => {
              setName(e.target.value);
              setProblem(undefined);
            }}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                cancel();
              }
            }}
          />
          <Button
            variant="link"
            size="sm"
            className="custom-marker-section-action"
            title={
              typing === 'rename' ? 'Save this name' : 'Add this section'
            }
            onClick={commit}
          >
            <FontAwesomeIcon icon="check" />
          </Button>
          <Button
            variant="link"
            size="sm"
            className="custom-marker-section-action custom-marker-section-cancel"
            title="Cancel"
            onClick={cancel}
          >
            <FontAwesomeIcon icon="times" />
          </Button>
        </div>
        {problem ? (
          <div className="custom-marker-section-problem">{problem}</div>
        ) : null}
      </div>
    );
  }

  // Only for a section of the practitioner's: a LabSmarts heading is not theirs
  // to rename, and the appendix has no name to correct.
  const canRename = !!(onRenameSection && selectedName);

  return (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
      <Form.Control
        as="select"
        className="custom-marker-input"
        style={{ ...(style || {}), flex: '1 1 auto' }}
        value={selectedValue}
        onChange={(e: any) => {
          if (e.target.value === NEW_SECTION_OPTION) {
            setName('');
            setTyping('new');
            return;
          }
          onChange(e.target.value);
        }}
      >
        {/* The default stays pinned at the top rather than sorting into the
            B's: it is where a marker goes when nothing is chosen, not one more
            section to scan past. */}
        <option value="">{CUSTOM_MARKER_OWN_SECTION_LABEL}</option>
        {byLabel(CUSTOM_MARKER_SECTION_OPTIONS).map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
        {options.length ? (
          <optgroup label="Your sections">
            {options.map(section => (
              <option
                key={section.id}
                value={practitionerSectionOptionValue(section.name)}
              >
                {section.name}
              </option>
            ))}
          </optgroup>
        ) : null}
        {onCreateSection ? (
          <option value={NEW_SECTION_OPTION}>{NEW_SECTION_OPTION_LABEL}</option>
        ) : null}
      </Form.Control>
      {canRename ? (
        <Button
          variant="link"
          size="sm"
          className="custom-marker-section-action custom-marker-section-rename"
          title={`Rename “${selectedName}” everywhere on this test`}
          onClick={() => {
            setName(selectedName!);
            setTyping('rename');
          }}
        >
          <FontAwesomeIcon icon="edit" />
        </Button>
      ) : null}
    </div>
  );
};

/**
 * The "add markers from My Markers" picker on Add Test Results.
 *
 * Replaced a flat dropdown of every definition the practitioner had ever
 * entered. That list was fine for the one-off marker it was built for and
 * hopeless for the case it actually gets used for: a fourteen-marker mold panel
 * meant fourteen trips through a dropdown that also listed everything else.
 *
 * So the unit here is the SECTION, opened folded with a count and an "Add all"
 * beside it. Individual markers are still one click away, for the panel where
 * only some of it was run.
 *
 * Deliberately stays open after an add. Practitioners run more than one add-on
 * panel, and a popover that closed on every pick would be the dropdown again.
 */
/**
 * The parenthetical after a marker's name in the picker.
 *
 * Units for a charted marker, and "text" for one reported in words — which has
 * none. Without it a urinalysis line and a charted marker whose units were
 * never filled in read identically, and the practitioner only finds out which
 * they picked once it is on the test.
 */
function pickerDetail(definition: CustomMarkerDefinition): string {
  if (isTextResult(definition)) {
    return ' (text)';
  }
  return definition.units ? ` (${definition.units})` : '';
}

/**
 * How the picker fits itself into the window.
 *
 * The popover opened below the button at a fixed size and stayed there, so a
 * button near the bottom of the screen put half the marker list past the edge
 * of the window and expanding a section pushed more of it out. Neither the page
 * nor the popover scrolled to it; the practitioner had to scroll the page down
 * to read a list that was supposed to be one click.
 *
 * So the picker measures before it opens: which side of the button has the room
 * and how much of it there is. The list gets that much height and no more, and
 * the section that expands scrolls INSIDE it rather than growing the popover
 * past the window.
 */
type PickerLayout = {
  placement: 'top-start' | 'bottom-start';
  /** Height for the scrolling list, the only part of the popover that grows. */
  maxHeight: number;
};

/** As tall as the list is allowed to get when there is room to spare. */
const PICKER_LIST_MAX_HEIGHT = 340;
/**
 * The least it may be squeezed to. Below about four rows the list stops being
 * a list, so a button with no room either side keeps this and accepts the
 * overflow rather than opening a picker nothing can be read in.
 */
const PICKER_LIST_MIN_HEIGHT = 150;
/** Title band, borders and body padding: the popover minus its list. */
const PICKER_CHROME_HEIGHT = 70;
/** The filter box and its margin, on the libraries big enough to show one. */
const PICKER_FILTER_HEIGHT = 37;
/** Clear of the button on one side and the window edge on the other. */
const PICKER_EDGE_GAP = 16;

function pickerLayout(trigger: HTMLElement | null, chrome: number): PickerLayout {
  if (!trigger) {
    return { placement: 'bottom-start', maxHeight: PICKER_LIST_MAX_HEIGHT };
  }
  const rect = trigger.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom - PICKER_EDGE_GAP;
  const above = rect.top - PICKER_EDGE_GAP;
  // Upward only when the room below cannot hold the picker AND there is more
  // of it above. A picker that opens upward from the middle of a screen with
  // room underneath it is its own kind of surprise.
  const up = below < PICKER_LIST_MAX_HEIGHT + chrome && above > below;
  const room = (up ? above : below) - chrome;
  return {
    placement: up ? 'top-start' : 'bottom-start',
    maxHeight: Math.max(
      PICKER_LIST_MIN_HEIGHT,
      Math.min(PICKER_LIST_MAX_HEIGHT, room),
    ),
  };
}

const MarkerPicker: React.FC<{
  library: CustomMarkerDefinition[];
  sections?: CustomMarkerSection[];
  /** Marker keys already on the test, so they read as added rather than absent. */
  inUse: Set<string>;
  onAdd: (definitions: CustomMarkerDefinition[]) => void;
  locked?: boolean;
}> = ({ library, sections, inUse, onAdd, locked }) => {
  const [filter, setFilter] = React.useState('');
  const [open, setOpen] = React.useState<{ [groupKey: string]: boolean }>({});
  const [layout, setLayout] = React.useState<PickerLayout>({
    placement: 'bottom-start',
    maxHeight: PICKER_LIST_MAX_HEIGHT,
  });
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  // Popper measures the popover once, when it opens, and never watches it
  // again. Expanding a section changes its height, so an upward-opening picker
  // would grow back down over the button it came from unless it is told.
  const repositionRef = React.useRef<(() => void) | null>(null);

  const showFilter = library.length > 6;

  React.useLayoutEffect(() => {
    if (repositionRef.current) {
      repositionRef.current();
    }
  }, [open, filter]);

  const text = filter.trim().toLowerCase();
  const matching = text
    ? library.filter(definition =>
        `${definition.name} ${definition.units || ''}`
          .toLowerCase()
          .includes(text),
      )
    : library;
  const groups = groupDefinitionsBySection(matching, sections);

  const remaining = (group: CustomMarkerSectionGroup<CustomMarkerDefinition>) =>
    group.items.filter(
      definition => !inUse.has(CustomMarker.keyFor(definition.name)),
    );

  return (
    <OverlayTrigger
      trigger="click"
      rootClose
      placement={layout.placement}
      overlay={(overlayProps: any) => {
        // The render-prop form of the overlay, for the one prop the element
        // form throws away: the handle that makes Popper measure again.
        const { show, scheduleUpdate, outOfBoundaries, ...popoverProps } =
          overlayProps;
        repositionRef.current = scheduleUpdate;
        return (
        <Popover
          {...popoverProps}
          id="custom-marker-picker"
          className="custom-marker-picker-popover"
        >
          <Popover.Title as="h6">Add from My Markers</Popover.Title>
          <Popover.Content>
            {showFilter ? (
              <Form.Control
                type="text"
                className="custom-marker-input"
                style={{ ...INPUT_STYLE, marginBottom: '6px' }}
                placeholder="Filter markers"
                value={filter}
                onChange={(e: any) => setFilter(e.target.value)}
              />
            ) : null}
            <div
              className="custom-marker-picker"
              style={{ maxHeight: `${layout.maxHeight}px` }}
            >
              {groups.length === 0 ? (
                <div style={{ fontSize: '12px', color: '#868e96' }}>
                  No markers match that filter.
                </div>
              ) : null}
              {groups.map(group => {
                const groupKey = sectionGroupKey(group.value);
                // Open when filtering: a filter is a search for a marker, and
                // folding the answer away is the dropdown's problem again.
                const expanded = text ? true : !!open[groupKey];
                const left = remaining(group);
                return (
                  <div key={groupKey} className="custom-marker-picker-section">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Button
                        variant="link"
                        size="sm"
                        className="custom-marker-group-toggle"
                        style={{ flex: '1 1 auto', textAlign: 'left' }}
                        onClick={() =>
                          setOpen({ ...open, [groupKey]: !expanded })
                        }
                      >
                        <FontAwesomeIcon
                          icon={expanded ? 'chevron-down' : 'chevron-right'}
                          style={{ fontSize: '10px' }}
                        />
                        &nbsp;&nbsp;
                        <span className="custom-marker-picker-name">
                          {group.label}
                        </span>
                        <span
                          style={{
                            fontSize: '11px',
                            color: '#868e96',
                            fontWeight: 400,
                          }}
                        >
                          &nbsp;&nbsp;{group.items.length}
                        </span>
                      </Button>
                      {/* The whole point of the picker. Named with the count so
                          it says what it is about to do. */}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        style={{ fontSize: '11px', padding: '1px 8px', whiteSpace: 'nowrap' }}
                        disabled={!locked && left.length === 0}
                        title={
                          left.length
                            ? `Add all ${left.length} to this test`
                            : 'Every marker in this section is already on the test'
                        }
                        onClick={() => onAdd(group.items)}
                      >
                        {left.length ? `Add all ${left.length}` : 'All added'}
                      </Button>
                    </div>
                    {expanded
                      ? group.items.map(definition => {
                          const added = inUse.has(
                            CustomMarker.keyFor(definition.name),
                          );
                          return (
                            <div
                              key={definition.id}
                              className={
                                added
                                  ? 'custom-marker-picker-marker is-in-use'
                                  : 'custom-marker-picker-marker'
                              }
                            >
                              {added ? (
                                <span>
                                  <FontAwesomeIcon icon="check" />
                                  &nbsp;{definition.name}
                                  {pickerDetail(definition)}
                                </span>
                              ) : (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="custom-marker-group-add"
                                  onClick={() => onAdd([definition])}
                                >
                                  + {definition.name}
                                  {pickerDetail(definition)}
                                </Button>
                              )}
                            </div>
                          );
                        })
                      : null}
                  </div>
                );
              })}
            </div>
          </Popover.Content>
        </Popover>
        );
      }}
    >
      {/* The span is what gets measured and what the popover anchors to. It
          wraps the button rather than replacing it so the measurement happens
          on the way down to the click that opens the picker, in time for the
          placement it opens at. */}
      <span
        ref={triggerRef}
        className="custom-marker-picker-trigger"
        onClickCapture={() =>
          setLayout(
            pickerLayout(
              triggerRef.current,
              PICKER_CHROME_HEIGHT + (showFilter ? PICKER_FILTER_HEIGHT : 0),
            ),
          )
        }
      >
        <Button
          variant={locked ? 'outline-secondary' : 'outline-primary'}
          size="sm"
          style={{ whiteSpace: 'nowrap' }}
        >
          {locked ? <FontAwesomeIcon icon="lock" /> : null}
          {locked ? ' ' : null}+ Add from My Markers
        </Button>
      </span>
    </OverlayTrigger>
  );
};

type Props = {
  rows: CustomMarkerRow[];
  onChange: (rows: CustomMarkerRow[]) => void;
  /**
   * Where the carried-forward rows came from, already phrased for the banner
   * ("this client's test on 08/05/2026", or "this client's previous tests" when
   * they were gathered from more than one). Only set when at least one row is
   * carried forward.
   */
  carriedForwardFrom?: string;
  /** The practitioner's own report sections, for the Section dropdown. */
  sections?: CustomMarkerSection[];
  /** Saves a section created from the Section dropdown to the profile. */
  onCreateSection?: (name: string) => void;
  /** Renames a section in the profile's list. The rows are renamed here. */
  onRenameSection?: (from: string, to: string) => void;
  /** The practitioner's My Markers library, for the picker. */
  library?: CustomMarkerDefinition[];
  /** The client's sex, so a definition with per-sex ranges resolves correctly. */
  clientGender?: string;
  /**
   * Whether this practitioner may ADD markers (UserProfile.canUseCustomMarkers).
   * When false the add controls stay visible but locked — rows already on the
   * test remain editable, and nothing already saved is affected.
   */
  customMarkersAllowed?: boolean;
  /** Called instead of adding when the feature is not enabled. */
  onUpgradeNeeded?: () => void;
  /**
   * Render ONLY this section group (a sectionGroupKey), as the body of that
   * section's own card on Add Test Results.
   *
   * A panel the practitioner runs whole deserves the weight the shipped panels
   * get: its own card, titled with its own name, sitting among them. The same
   * rows grouped inside an appendix card headed "Add Your Own Markers to This
   * Test" read as an afterthought, which is not what a fourteen-marker mold
   * panel is.
   *
   * Rows are still addressed by their index in the FULL array, so one onChange
   * contract covers every card and nothing has to be stitched back together.
   */
  onlyGroupKey?: string;
  /** Render everything EXCEPT these groups, which have cards of their own. */
  exceptGroupKeys?: string[];
  /**
   * Section values whose markers show their full range rather than folding
   * (UserProfile.expandedTestSections).
   *
   * Folding suits a panel entered from memory and is wrong for one checked
   * against the lab report line by line — clicking a caret per marker is not an
   * answer to that, so the practitioner sets it per section in My Markers.
   */
  expandedSections?: string[];
};

const RANGE_COLUMNS: { key: CustomMarkerRangeKey; label: string; required?: boolean }[] = [
  { key: 'alarmMin', label: 'Alarm Min' },
  { key: 'labMin', label: 'Lab Min', required: true },
  { key: 'optimalMin', label: 'Optimal Min' },
  { key: 'optimalMax', label: 'Optimal Max' },
  { key: 'labMax', label: 'Lab Max', required: true },
  { key: 'alarmMax', label: 'Alarm Max' },
];

/** Exported so My Markers marks its required columns identically. */
export const RequiredMark: React.FC = () => (
  <span style={{ color: '#c0392b' }} title="Required"> *</span>
);

const HEADER_STYLE: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#495057',
  whiteSpace: 'nowrap',
  padding: '4px 6px',
  verticalAlign: 'bottom',
};

/*
 * verticalAlign: 'top' because a cell can be taller than its neighbors and must
 * not drag its input off their line when it is.
 *
 * The Marker Name cell grows: "Hide ranges" sits under the input on an opened
 * library row, and the "LabSmarts already has a field" warning sits under it on
 * a typed one. A table cell centers its contents by default, so that extra
 * height lifted the name box a dozen pixels above every other box in the row.
 */
const CELL_STYLE: React.CSSProperties = {
  padding: '3px 6px',
  verticalAlign: 'top',
};

const INPUT_STYLE: React.CSSProperties = { fontSize: '13px', height: '31px' };

/*
 * The six range columns, narrower than the columns either side of them because
 * six of them in a row are what decide whether the table fits its card: at 90px
 * apiece the row was a few dozen pixels too wide on a laptop and the whole table
 * grew a horizontal scroll bar the moment a marker or a text result was added.
 *
 * A boundary is four or five characters, so the width lost here is width the
 * numbers never used — and .custom-marker-range-input trims the input's own side
 * padding to keep the same room for typing inside the smaller box.
 *
 * Two of the columns ("Optimal Min", "Optimal Max") sit a little wider than this
 * anyway: their headers do not wrap, so the label is their real floor.
 */
const RANGE_COLUMN_MIN_WIDTH = '76px';

/*
 * The reorder column, as narrow as a 10px handle and a 12px icon can be carried
 * in: 4px of gutter, the control, and nothing else.
 *
 * It is measured rather than eyeballed for the reason the range columns above
 * are. This table is tuned to fit its card exactly, and the FIRST version of
 * this column (30px) put it 8px over — enough for a horizontal scroll bar to
 * appear across the card with almost nothing to scroll to. Same widths on the
 * header and the body cells, so the icon sits over the handles.
 */
const REORDER_COLUMN_STYLE: React.CSSProperties = {
  width: '18px',
  paddingLeft: '4px',
  paddingRight: 0,
};

/**
 * The "Add Your Own Markers to This Test" table on Add/Edit Test Results.
 *
 * Plain always-editable Bootstrap inputs, matching the rest of the entry form.
 * (src/components/common/editable-cell.tsx exists but is click-to-edit, which
 * is the wrong interaction for typing a full row of numbers.)
 */
/**
 * View state for the entry table. None of it is persisted or saved with the
 * test: which sections are folded and which rows are opened up is about reading
 * the screen, not about the results on it.
 */
type CustomMarkersTableState = {
  /** Section headings folded shut, keyed by sectionGroupKey. */
  collapsedGroups: { [groupKey: string]: boolean };
  /**
   * Library rows opened back up to their full width, for correcting a range on
   * this one test.
   *
   * Keyed by the MARKER, not by its position. Position was wrong twice over
   * once the named sections moved to cards of their own: each card is its own
   * component with its own map, but the indexes in it point into the one shared
   * rows array — so removing a row in one card shifted every row below it and
   * left the sibling cards' maps pointing at the wrong markers. Names are
   * unique on a test (validation refuses a duplicate), so they survive both
   * removals and the split across cards.
   */
  expandedRows: { [markerKey: string]: boolean };
  /** The row being dragged by its handle, and the row it is currently over. */
  dragIndex: number | null;
  dropIndex: number | null;
};

export class CustomMarkersTable extends React.Component<
  Props,
  CustomMarkersTableState
> {
  state: CustomMarkersTableState = {
    collapsedGroups: {},
    expandedRows: {},
    dragIndex: null,
    dropIndex: null,
  };

  private update(index: number, key: keyof CustomMarkerRow, value: string) {
    const rows = this.props.rows.map((row, i) =>
      i === index ? { ...row, [key]: value } : row,
    );
    this.props.onChange(rows);
  }

  /**
   * True when the practitioner may add markers. Undefined means "not gated",
   * so the table behaves normally anywhere the flag isn't threaded through.
   */
  private get allowed(): boolean {
    return this.props.customMarkersAllowed !== false;
  }

  /** Locked: explain instead of adding. Returns true when it handled the click. */
  private blockedByEntitlement(): boolean {
    if (this.allowed) {
      return false;
    }
    if (this.props.onUpgradeNeeded) {
      this.props.onUpgradeNeeded();
    }
    return true;
  }

  /** Add a blank row, optionally already filed into a section. */
  private addRow = (
    sectionValue?: string,
    resultType: CustomMarkerResultType = CustomMarkerResultType.NUMERIC,
  ) => {
    if (this.blockedByEntitlement()) {
      return;
    }
    this.props.onChange([
      ...this.props.rows,
      {
        ...emptyCustomMarkerRow(),
        resultType,
        ...(sectionValue ? { section: sectionValue } : {}),
      },
    ]);
  };

  /**
   * Append a My Markers definition, resolved for this client. Reuses the single
   * blank starter row rather than leaving it stranded above the pick.
   */
  /**
   * Add library definitions to the test: one marker, or a whole section at once.
   *
   * Through the shared addDefinitionsToRows, so a section added here lands
   * exactly as the same section does when it loads automatically as one of the
   * practitioner's defaults.
   */
  private addDefinitions = (definitions: CustomMarkerDefinition[]) => {
    if (this.blockedByEntitlement()) {
      return;
    }
    const { rows, clientGender, onChange } = this.props;
    const next = addDefinitionsToRows(rows, definitions, clientGender, {
      byHand: true,
    });
    if (next !== rows) {
      onChange(next);
    }
  };

  /**
   * Reorder within the section. Both the handle's arrow keys and a dropped drag
   * end up here.
   */
  private moveRow = (fromIndex: number, toIndex: number, slots: number[]) => {
    if (this.blockedByEntitlement()) {
      return;
    }
    const next = moveRowWithin(this.props.rows, slots, fromIndex, toIndex);
    if (next !== this.props.rows) {
      this.props.onChange(next);
    }
  };

  /**
   * One place up or down the section, for the arrow keys.
   *
   * A pointer is not the only way to sort a list, and it is the wrong way to
   * sort a fourteen-row panel by one place. Steps to the NEIGHBORING SLOT
   * rather than to index ± 1, because the section's rows need not be adjacent
   * in the array.
   */
  private moveRowBy = (index: number, slots: number[], step: number) => {
    const ordered = [...slots].sort((a, b) => a - b);
    const target = ordered[ordered.indexOf(index) + step];
    if (!ordered.includes(index) || target === undefined) {
      return;
    }
    this.moveRow(index, target, ordered);
  };

  private onDragStart = (
    event: React.DragEvent<HTMLElement>,
    index: number,
  ) => {
    if (!this.allowed) {
      event.preventDefault();
      return;
    }
    // Firefox drags nothing at all without payload data set.
    event.dataTransfer.effectAllowed = 'move';
    try {
      event.dataTransfer.setData('text/plain', String(index));
    } catch (e) {
      // IE-ism; the drag still works off component state below.
    }
    // The handle is a 12px square, and a 12px square is not a legible thing to
    // drag a row by. Drag the ROW's own image instead.
    const row = event.currentTarget.closest('tr');
    if (row && event.dataTransfer.setDragImage) {
      event.dataTransfer.setDragImage(row, 24, row.clientHeight / 2);
    }
    this.setState({ dragIndex: index, dropIndex: null });
  };

  private onDragOverRow = (
    event: React.DragEvent<HTMLElement>,
    index: number,
  ) => {
    if (this.state.dragIndex === null) {
      return;
    }
    // Without this the drop never fires: the default handling of a dragover is
    // to refuse the drop.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (this.state.dropIndex !== index) {
      this.setState({ dropIndex: index });
    }
  };

  private onDropRow = (
    event: React.DragEvent<HTMLElement>,
    index: number,
    slots: number[],
  ) => {
    event.preventDefault();
    const { dragIndex } = this.state;
    this.setState({ dragIndex: null, dropIndex: null });
    if (dragIndex !== null) {
      this.moveRow(dragIndex, index, slots);
    }
  };

  private onDragEnd = () => {
    this.setState({ dragIndex: null, dropIndex: null });
  };

  /**
   * The class that draws where the row would land: on the edge it would arrive
   * at, so the line is between the two rows it would end up between.
   */
  private dropClass(index: number): string {
    const { dragIndex, dropIndex } = this.state;
    if (dragIndex === null || dropIndex !== index || dragIndex === index) {
      return dragIndex === index ? 'custom-marker-row-dragging' : '';
    }
    return dragIndex < index
      ? 'custom-marker-row-drop-below'
      : 'custom-marker-row-drop-above';
  }

  /**
   * The reorder handle.
   *
   * Its own column rather than an affordance inside the name cell: this is the
   * order the report prints in, and a control for that should be visible
   * without hovering to discover it. Rendered even for a section of one, as an
   * empty cell, so the column does not appear and disappear down the card.
   */
  private renderDragCell(index: number, slots: number[], column: boolean) {
    // No column at all on a table with nothing to reorder: 22px of dead width
    // in front of Marker Name pushed the first column out of line with
    // everything else on the card, and with one row it sat under a help icon
    // explaining a handle that was not there.
    if (!column) {
      return null;
    }
    const sortable = slots.length > 1 && this.allowed;
    return (
      <td
        key="drag"
        className="custom-marker-drag-cell"
        style={{ ...CELL_STYLE, ...REORDER_COLUMN_STYLE }}
      >
        {sortable ? (
          <span
            className="custom-marker-drag-handle"
            role="button"
            tabIndex={0}
            draggable
            aria-label="Reorder this marker"
            title="Drag to reorder, or use the up and down arrow keys. This is the order the markers are displayed in."
            onDragStart={e => this.onDragStart(e, index)}
            onDragEnd={this.onDragEnd}
            onKeyDown={e => {
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                this.moveRowBy(index, slots, -1);
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                this.moveRowBy(index, slots, 1);
              }
            }}
          />
        ) : null}
      </td>
    );
  }

  private removeRow(index: number) {
    const rows = this.props.rows.filter((_, i) => i !== index);
    // No remapping needed: expansion is keyed by marker, so nothing below
    // slides into anybody else's state.
    this.props.onChange(rows.length ? rows : [emptyCustomMarkerRow()]);
  }

  /**
   * A library row still showing only what changes per test.
   *
   * Requires units, because the folded row has no box to type them into and the
   * save validator does require them. My Markers refuses to save a definition
   * without units, so this only ever catches one saved before that rule went
   * in — which would otherwise fail Save pointing at a field that is not on
   * screen.
   */
  private isCompact(row: CustomMarkerRow, index: number): boolean {
    if (isExpandedTestSection(this.props.expandedSections, row.section)) {
      return false;
    }
    // A row with no name has nothing for the folded layout to show and no box
    // to type a name into, so it can never be the row that folds — whatever
    // flag it is carrying.
    if (!(row.name || '').trim()) {
      return false;
    }
    return (
      !!row.carriedForward &&
      // Units used to be required here: a folded row has no box to type them
      // into, so a row missing them could not be allowed to hide the field it
      // was going to fail validation on. They are optional now (pH has none),
      // which makes blank a legitimate answer rather than an unfinished row,
      // and a unitless marker folds like any other.
      !this.state.expandedRows[CustomMarker.keyFor(row.name)]
    );
  }

  /**
   * Apply a Chart Type pick, which is also where a marker is switched between a
   * number and words. Both fields move together so a row can never be left
   * saying "text" while still carrying a chart shape, or the reverse.
   */
  private setResultShape = (index: number, value: string) => {
    const fields = resultShapeFields(value);
    this.props.onChange(
      this.props.rows.map((row, i) => (i === index ? { ...row, ...fields } : row)),
    );
  };

  private toggleRow(row: CustomMarkerRow) {
    const key = CustomMarker.keyFor(row.name);
    this.setState(state => ({
      expandedRows: { ...state.expandedRows, [key]: !state.expandedRows[key] },
    }));
  }

  /**
   * Correct a section's name on EVERY row of this test that uses it, not just
   * the row the pencil was clicked on.
   *
   * A marker carries the name it was saved with, which is what stops a rename
   * from retitling a report already issued — but it also means a typo caught
   * after entering a fourteen-marker panel would otherwise be fourteen
   * corrections. The profile's own list is renamed by the caller.
   */
  private renameSection = (from: string, to: string) => {
    const fromKey = practitionerSectionKey(from);
    const next = practitionerSectionOptionValue(to);
    this.props.onChange(
      this.props.rows.map(row =>
        practitionerSectionKey(
          practitionerSectionFromOptionValue(row.section),
        ) === fromKey
          ? { ...row, section: next }
          : row,
      ),
    );
    if (this.props.onRenameSection) {
      this.props.onRenameSection(from, to);
    }
  };

  /**
   * The value that will actually be used for a blank optional boundary, shown
   * as placeholder text so the practitioner can see what gets derived. Built
   * through the very same CustomMarkerRange the report uses, so the preview
   * can never drift from what is charted.
   */
  private derivedPlaceholder(
    row: CustomMarkerRow,
    key: CustomMarkerRangeKey,
  ): string {
    return derivedRangeValue(row, row.chartType, precisionForRow(row), key);
  }

  /**
   * A section heading across the table.
   *
   * Only drawn when the test really has more than one section on it (see
   * `grouped` in render): a heading over the two loose markers this table was
   * originally built for is a label nobody needed.
   */
  private renderGroupHeading(
    group: CustomMarkerSectionGroup<{ row: CustomMarkerRow; index: number }>,
    groupKey: string,
    folded: boolean,
  ) {
    const count = group.items.length;

    return (
      <tr key={`${groupKey}-heading`} className="custom-marker-group-heading">
        <td colSpan={RANGE_COLUMNS.length + 7} style={{ padding: '10px 6px 4px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="link"
              size="sm"
              className="custom-marker-group-toggle"
              title={folded ? `Show ${group.label}` : `Hide ${group.label}`}
              onClick={() =>
                this.setState(state => ({
                  collapsedGroups: {
                    ...state.collapsedGroups,
                    [groupKey]: !folded,
                  },
                }))
              }
            >
              <FontAwesomeIcon
                icon={folded ? 'chevron-right' : 'chevron-down'}
                style={{ fontSize: '11px' }}
              />
              &nbsp;&nbsp;{group.label}
            </Button>
            <span className="custom-marker-group-count">
              {count} marker{count === 1 ? '' : 's'}
            </span>
            {/* Sections the practitioner named have cards of their own on this
                screen, so anything still headed inside this card is one of
                LabSmarts' — worth saying, since it is the difference between a
                heading they can rename and one they cannot.

                The appendix gets neither label: the markers in it are theirs
                but the section is not, so both readings are wrong. See the
                same rule in custom-marker-library. */}
            {group.kind === 'own' ? null : (
              <span className="custom-marker-group-origin">
                {group.kind === 'practitioner'
                  ? 'Your section'
                  : 'LabSmarts section'}
              </span>
            )}
          </div>
        </td>
      </tr>
    );
  }

  /**
   * A library row showing only what changes from one test to the next.
   *
   * Everything else about the marker (its chart type, its section, its six
   * boundaries) was settled in My Markers and does not want re-reading fourteen
   * times down a mold panel. The caret opens the full row for the one test
   * where a range needs correcting.
   */
  private renderCompactRow(
    row: CustomMarkerRow,
    index: number,
    slots: number[],
    dragColumn: boolean,
  ) {
    const isText = isTextResult(row);
    const expected = (row.expectedText || '').trim();
    return (
      <tr
        key={index}
        className={this.dropClass(index)}
        onDragOver={e => this.onDragOverRow(e, index)}
        onDrop={e => this.onDropRow(e, index, slots)}
      >
        {this.renderDragCell(index, slots, dragColumn)}
        <td style={CELL_STYLE}>
          <div className="custom-marker-compact-name" title={row.name}>
            {row.name}
          </div>
        </td>
        {/* Widened over the Units column on a text row, for the reason the
            open row is: the words are the result, and they need the room. */}
        <td style={CELL_STYLE} colSpan={isText ? 2 : undefined}>
          <Form.Control
            type="text"
            className="custom-marker-input"
            style={INPUT_STYLE}
            value={row.value}
            maxLength={isText ? CUSTOM_MARKER_TEXT_MAX_LENGTH : undefined}
            placeholder={valuePlaceholder(row)}
            aria-label={`${row.name} result`}
            onChange={(e: any) => this.update(index, 'value', e.target.value)}
          />
        </td>
        {isText ? null : (
          <td style={CELL_STYLE}>
            <div className="custom-marker-compact-units">{row.units}</div>
          </td>
        )}
        {/* Chart Type, Section and the six boundaries, all already answered. */}
        <td colSpan={RANGE_COLUMNS.length + 2} style={CELL_STYLE}>
          <Button
            variant="link"
            size="sm"
            className="custom-marker-row-expand"
            title={
              isText
                ? "Show this marker's expected result and section, to change them for this test only"
                : "Show this marker's chart type, section and reference range, to change them for this test only"
            }
            onClick={() => this.toggleRow(row)}
          >
            <FontAwesomeIcon icon="chevron-down" style={{ fontSize: '10px' }} />
            &nbsp;&nbsp;
            {isText ? 'Text marker' : 'Ranges from My Markers'}
          </Button>
          {/* Worth showing folded: it is the one thing that decides whether the
              result just typed gets marked on the report. */}
          {isText && expected ? (
            <span className="custom-marker-compact-expected">
              Expected: {expected}
            </span>
          ) : null}
        </td>
        <td style={{ ...CELL_STYLE, textAlign: 'center' }}>
          <Button
            variant="link"
            size="sm"
            title="Remove this marker"
            style={{ color: '#766d66', padding: '0 4px' }}
            onClick={() => this.removeRow(index)}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </td>
      </tr>
    );
  }

  /**
   * A row with every column open: the marker, its result, and the six range
   * boundaries. What a marker typed by hand needs, and what a library marker
   * shows once its caret is opened to correct a range for this one test.
   */
  private renderFullRow(
    row: CustomMarkerRow,
    index: number,
    slots: number[],
    dragColumn: boolean,
  ) {
    const isText = isTextResult(row);
    // Not merely "came from the library": a row with no units can never fold
    // (see isCompact), so offering to fold it is an inert control. A text row
    // folds on the same terms it is compacted on — units never enter into it.
    const fromLibrary =
      !!row.carriedForward && (isText || !!(row.units || '').trim());
    return (
                <tr
                  key={index}
                  className={this.dropClass(index)}
                  onDragOver={e => this.onDragOverRow(e, index)}
                  onDrop={e => this.onDropRow(e, index, slots)}
                >
                  {this.renderDragCell(index, slots, dragColumn)}
                  <td style={CELL_STYLE}>
                    <Form.Control
                      type="text"
                      className="custom-marker-input"
                      style={INPUT_STYLE}
                      value={row.name}
                      maxLength={60}
                      placeholder={`e.g. ${
                        isText
                          ? MARKER_NAME_PLACEHOLDER_TEXT_EXAMPLE
                          : MARKER_NAME_PLACEHOLDER_EXAMPLE
                      }`}
                      onChange={(e: any) =>
                        this.update(index, 'name', e.target.value)
                      }
                    />
                    {/* A hint, not an error: entering it here still works, and
                        the practitioner may have a reason. It just costs them
                        the analysis, which nothing else on this screen says. */}
                    {fromLibrary ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="custom-marker-row-expand"
                        title="Fold this marker back to just its result"
                        onClick={() => this.toggleRow(row)}
                      >
                        <FontAwesomeIcon
                          icon="chevron-up"
                          style={{ fontSize: '10px' }}
                        />
                        &nbsp;&nbsp;Hide ranges
                      </Button>
                    ) : null}
                    {shippedMarkerNamed(row.name, PanelField.PANEL_MARKER_FIELDS) ? (
                      <div className="custom-marker-shipped-warning">
                        <FontAwesomeIcon icon="info-circle" />
                        &nbsp;LabSmarts already has a field for this marker in
                        the panels above. Enter the result there instead.
                      </div>
                    ) : null}
                  </td>
                  {/* A text result has no units, so its box takes that column
                      too. Words need the room a cell sized for a number does
                      not have ("e.g. Negative" was clipped to "e.g. Nega"), and
                      the alternative spent the width on a dash saying there is
                      nothing to enter. The Chart Type cell that follows keeps
                      the rest of the row lined up down a mixed panel. */}
                  <td style={CELL_STYLE} colSpan={isText ? 2 : undefined}>
                    <Form.Control
                      type="text"
                      className="custom-marker-input"
                      style={INPUT_STYLE}
                      value={row.value}
                      maxLength={isText ? CUSTOM_MARKER_TEXT_MAX_LENGTH : undefined}
                      // Only on a row still waiting for its name. Then the
                      // three boxes read across as one worked example
                      // (Lipase, 50, U/L). Once the marker IS named — a row
                      // picked from My Markers, or one being typed — a lipase
                      // result suggested beside "Zinc, RBC" is an example of
                      // the wrong thing.
                      placeholder={valuePlaceholder(row)}
                      onChange={(e: any) =>
                        this.update(index, 'value', e.target.value)
                      }
                    />
                  </td>
                  {isText ? null : (
                    <td style={CELL_STYLE}>
                      <Form.Control
                        type="text"
                        className="custom-marker-input"
                        style={INPUT_STYLE}
                        value={row.units}
                        maxLength={20}
                        placeholder={`e.g. ${MARKER_UNITS_PLACEHOLDER_EXAMPLE}`}
                        onChange={(e: any) =>
                          this.update(index, 'units', e.target.value)
                        }
                      />
                    </td>
                  )}
                  <td style={CELL_STYLE}>
                    <Form.Control
                      as="select"
                      className="custom-marker-input"
                      style={INPUT_STYLE}
                      value={resultShapeValue(row)}
                      onChange={(e: any) =>
                        this.setResultShape(index, e.target.value)
                      }
                    >
                      {CUSTOM_MARKER_RESULT_SHAPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Control>
                  </td>
                  <td style={CELL_STYLE}>
                    <SectionSelect
                      style={INPUT_STYLE}
                      value={row.section}
                      onChange={next => this.update(index, 'section', next)}
                      sections={this.props.sections}
                      onCreateSection={this.props.onCreateSection}
                      onRenameSection={this.renameSection}
                    />
                  </td>
                  {/* A text marker has no boundaries to enter, so the six
                      range columns become the one thing it CAN be given: what
                      the practitioner expects it to read. Spanning the columns
                      rather than adding a thirteenth keeps every row on this
                      table the same width, mixed panels included. */}
                  {isText ? (
                    <td colSpan={RANGE_COLUMNS.length} style={CELL_STYLE}>
                      <div className="custom-marker-expected-cell">
                        <span className="custom-marker-expected-label">
                          Expected result (optional)
                        </span>
                        <Form.Control
                          type="text"
                          className="custom-marker-input"
                          style={INPUT_STYLE}
                          value={row.expectedText}
                          maxLength={CUSTOM_MARKER_EXPECTED_MAX_LENGTH}
                          placeholder="e.g. Negative"
                          title="Leave blank and the result is simply printed. Fill it in and a result that reads differently is marked on the report."
                          onChange={(e: any) =>
                            this.update(index, 'expectedText', e.target.value)
                          }
                        />
                        {/* No "differs from expected" note here. Mid-entry the
                            row is half-typed, so a note comparing the two boxes
                            fires on a state the practitioner is still in the
                            middle of leaving, and it reads as an error on a row
                            that saves fine. The disagreement is marked where it
                            is actually being read: the analysis report and the
                            comparison table. */}
                      </div>
                    </td>
                  ) : RANGE_COLUMNS.map(col => {
                    // A blank optional boundary shows the number LabSmarts will
                    // calculate, on a gray field, so it can't be mistaken for
                    // something the practitioner entered.
                    //
                    // Lab Max is normally required, but on a row charted as
                    // "only low is a concern" the lab range is open-ended and
                    // Optimal Min supplies the width instead — so for that row
                    // Lab Max becomes another calculated cell.
                    const isCalculatedLabMax =
                      col.key === 'labMax' &&
                      row.chartType === CustomMarkerChartType.HIGHER_IS_BETTER;
                    const derived =
                      col.required && !isCalculatedLabMax
                        ? ''
                        : this.derivedPlaceholder(row, col.key);
                    const isDerived = !row[col.key].trim() && !!derived;
                    return (
                      <td key={col.key} style={CELL_STYLE}>
                        <Form.Control
                          type="text"
                          className={
                            isDerived
                              ? 'custom-marker-input custom-marker-range-input custom-marker-derived'
                              : 'custom-marker-input custom-marker-range-input'
                          }
                          style={INPUT_STYLE}
                          value={row[col.key]}
                          placeholder={derived}
                          title={
                            isDerived
                              ? `Calculated from the lab range. Type here to set your own ${col.label.toLowerCase()}.`
                              : undefined
                          }
                          onChange={(e: any) =>
                            this.update(index, col.key, e.target.value)
                          }
                        />
                      </td>
                    );
                  })}

                  <td style={{ ...CELL_STYLE, textAlign: 'center' }}>
                    <Button
                      variant="link"
                      size="sm"
                      title="Remove this marker"
                      style={{ color: '#766d66', padding: '0 4px' }}
                      onClick={() => this.removeRow(index)}
                    >
                      <FontAwesomeIcon icon="trash-alt" />
                    </Button>
                  </td>
                </tr>
    );
  }

  /**
   * The one line under the table, on the appendix card and on every section
   * card alike.
   *
   * Carries the override, not only the formula. It said just how the bounds are
   * derived, which reads as a closed system: gray, holding a number nobody
   * typed, is exactly what a read-only cell looks like. The permission to type
   * over it existed only in the ⓘ popover and in each cell's hover title, and a
   * tooltip on a cell you believe is read-only reaches nobody who has not
   * already guessed. This is the only text about Optimal and Alarm that is on
   * screen without a click, and it sits right under the numbers it explains.
   *
   * Section cards had no note at all, so a practitioner whose markers all live
   * in named sections read nothing about either half of this.
   */
  private renderCalculatedNote() {
    return (
      /* NOT nowrap. The sentence no longer fits on one line beside the two
         buttons at card width, and what actually keeps the button labels from
         breaking is their own flex: 0 0 auto, not this. */
      <span style={{ fontSize: '12px', color: '#6c757d' }}>
        {!this.allowed
          ? 'Adding your own markers is not enabled on this account.'
          : 'Optimal and Alarm are calculated at 1 and 3 standard deviations from the middle of the lab range. Type in a gray cell to set your own.'}
      </span>
    );
  }

  /**
   * The column header row, shared by the appendix card and every section card.
   *
   * The section cards each draw their own table, so this cannot live inline in
   * one of them: two headers drifting apart is how a Lab Max column ends up
   * labeled Lab Min on the card nobody looked at.
   */
  private renderHead(compactOnly: boolean, dragColumn: boolean) {
    // Every row folded means eight of the eleven columns are empty, and a
    // column header over nothing is the same dead width this whole redesign
    // was about. The wide head comes back the moment a row is opened.
    if (compactOnly) {
      return (
        <thead>
          <tr>
            {dragColumn ? (
              <th
                className="custom-marker-drag-cell"
                style={{ ...HEADER_STYLE, ...REORDER_COLUMN_STYLE }}
              />
            ) : null}
            <th style={{ ...HEADER_STYLE, minWidth: '190px' }}>
              Marker Name
              <RequiredMark />
              {/* Beside the heading, the way Chart Type's is. It used to sit
                  in the reorder column, where it read as a Marker Name help
                  icon that had wandered one column left — and on a table with
                  nothing to reorder it was the only thing in a column that
                  then indented Marker Name past everything else on the
                  card. */}
              {dragColumn ? <ReorderHelp /> : null}
            </th>
            <th style={{ ...HEADER_STYLE, minWidth: '90px' }}>
              Value
              <RequiredMark />
            </th>
            <th style={{ ...HEADER_STYLE, minWidth: '95px' }}>Units</th>
            {/* width:100% so the spare space lands HERE rather than being
                shared out among the three real columns — otherwise a result
                box for a two-digit number stretches to a third of the card. */}
            <th
              colSpan={RANGE_COLUMNS.length + 2}
              style={{ ...HEADER_STYLE, width: '100%' }}
            />
            <th style={{ ...HEADER_STYLE, width: '34px' }} />
          </tr>
        </thead>
      );
    }
    return (
      <thead>
        <tr>
          {/* The reorder handles. No word over them: "Order" as a heading
              says only what the handles already look like, and the column is
              26px wide. What the handle cannot say is carried by the help icon
              beside Marker Name. */}
          {dragColumn ? (
            <th
              className="custom-marker-drag-cell"
              style={{ ...HEADER_STYLE, ...REORDER_COLUMN_STYLE }}
            />
          ) : null}
          <th style={{ ...HEADER_STYLE, minWidth: '190px' }}>
            Marker Name
            <RequiredMark />
            {dragColumn ? <ReorderHelp /> : null}
          </th>
          <th style={{ ...HEADER_STYLE, minWidth: '90px' }}>
            Value
            <RequiredMark />
          </th>
          {/* No required mark: some markers have none. pH, a ratio and an
              index are all reported as a bare number, and LabSmarts ships one
              itself (RPI, units: ''). Demanding units here is what put "%" on a
              pH row and printed "7 %" on a client's report. */}
          <th style={{ ...HEADER_STYLE, minWidth: '95px' }}>Units</th>
          <th style={{ ...HEADER_STYLE, minWidth: '215px' }}>
            Chart Type
            <RequiredMark />
            <ChartTypeHelp />
          </th>
          <th style={{ ...HEADER_STYLE, minWidth: '215px' }}>Section</th>
          {RANGE_COLUMNS.map(col => (
            <th key={col.key} style={{ ...HEADER_STYLE, minWidth: RANGE_COLUMN_MIN_WIDTH }}>
              {col.label}
              {col.required ? <RequiredMark /> : null}
            </th>
          ))}
          <th style={{ ...HEADER_STYLE, width: '34px' }} />
        </tr>
      </thead>
    );
  }

  render() {
    const { rows, carriedForwardFrom, library } = this.props;
    const inUse = rowKeysInUse(rows);
    const hasLibrary = !!(library && library.length);
    const locked = !this.allowed;
    // Grouped through the shared helper, so the entry table, My Markers and the
    // picker all put the sections in the same order.
    const groups = groupBySectionValue(
      rows.map((row, index) => ({ row, index })),
      entry => entry.row.section,
      this.props.sections,
    );
    // A heading wherever the markers are filed somewhere NAMED, not only when
    // two sections happen to be present.
    //
    // The rule was "more than one group", which suppressed the heading in the
    // single most common case this feature exists for: one panel loaded onto a
    // new client because its section is one of the practitioner's defaults.
    // Fourteen mold markers arrived as a bare list of names with the word
    // "Mycotoxins" nowhere on the screen, so the panel looked like it had not
    // loaded at all.
    //
    // The original intent survives: the appendix standing alone is still
    // unlabeled, because "Other Lab Results" over the two loose
    // markers this table was built for is a label that explains nothing.
    // Deliberately computed over what this card actually SHOWS (see `shown`
    // below), not over every group on the test: with the named sections moved
    // to cards of their own, the appendix is left alone in this one and a
    // heading over it is the label that explains nothing again.
    const groupedFor = (
      visible: CustomMarkerSectionGroup<{ row: CustomMarkerRow; index: number }>[],
    ) =>
      visible.length > 1 ||
      (visible.length === 1 && visible[0].kind !== 'own');

    // A card of its own carries the section's name in its header, so the table
    // inside it drops the heading, the intro, the picker and the footer note:
    // the card IS the chrome.
    const { onlyGroupKey, exceptGroupKeys } = this.props;
    const shown = onlyGroupKey
      ? groups.filter(group => sectionGroupKey(group.value) === onlyGroupKey)
      : groups.filter(
          group =>
            !(exceptGroupKeys || []).includes(sectionGroupKey(group.value)),
        );

    // Off the rows THIS card shows, not every row on the test. The banner used
    // to be gated by the parent over the whole set, so once the practitioner's
    // own sections moved to cards of their own it kept announcing
    // carried-forward rows above the appendix table, which by then held none of
    // them and was often not drawn at all.
    const carriedShown = shown.some(group =>
      group.items.some(({ row }) => row.carriedForward),
    );
    // Provenance only when there is provenance worth printing. A defaulted
    // panel names no source (its section is a card on this screen already) and
    // keeps just the rule that matters: blanks are dropped, not saved empty.
    //
    // The rule is stated as an INSTRUCTION, not as an explanation of why a row
    // is blank. "Anything left blank was not on this panel" told the
    // practitioner what their own blank means, which is not something this
    // screen knows: a row can be blank because the panel did not include it,
    // because the lab has not reported it yet, or because they have not typed
    // it in. Only the consequence is ours to state.
    const carryForwardNote =
      !carriedShown ? null : (
        <div className="custom-marker-carry-forward-note">
          <FontAwesomeIcon icon="info-circle" />
          &nbsp;&nbsp;
          {carriedForwardFrom ? (
            <>
              Brought forward from <strong>{carriedForwardFrom}</strong>.{' '}
            </>
          ) : null}
          Add only the values you have for this test. Anything left blank is not
          saved.
        </div>
      );

    // Nothing to reorder means no reorder column: see renderDragCell. Worked
    // out per TABLE, so a one-marker section beside a fourteen-marker one still
    // lines its rows up under the same columns.
    const appendixDragColumn =
      this.allowed && shown.some(group => group.items.length > 1);

    if (onlyGroupKey) {
      const [group] = shown;
      // The section's last marker was just deleted. The card unmounts on the
      // next render; drawing an empty table for one frame is worse than
      // drawing nothing.
      if (!group) {
        return null;
      }
      // One marker in the section is nothing to reorder, so the column and its
      // help icon both stay away and Marker Name starts where the rest of the
      // card does.
      const dragColumn = group.items.length > 1 && this.allowed;
      return (
        <div>
          {carryForwardNote}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
              {this.renderHead(
                group.items.every(({ row, index }) => this.isCompact(row, index)),
                dragColumn,
              )}
              <tbody>
                {group.items.map(({ row, index }, _i, items) =>
                  this.isCompact(row, index)
                    ? this.renderCompactRow(
                        row,
                        index,
                        items.map(i => i.index),
                        dragColumn,
                      )
                    : this.renderFullRow(
                        row,
                        index,
                        items.map(i => i.index),
                        dragColumn,
                      ),
                )}
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              marginTop: '8px',
            }}
          >
            <Button
              variant={locked ? 'outline-secondary' : 'outline-primary'}
              size="sm"
              style={{ whiteSpace: 'nowrap' }}
              title={
                locked
                  ? 'Custom markers are not enabled on this account'
                  : undefined
              }
              onClick={() => this.addRow(group.value)}
            >
              {locked ? <FontAwesomeIcon icon="lock" /> : null}
              {locked ? ' ' : null}+ Add a numeric marker
            </Button>
            <Button
              variant={locked ? 'outline-secondary' : 'outline-primary'}
              size="sm"
              style={{ whiteSpace: 'nowrap' }}
              title={
                locked
                  ? 'Custom markers are not enabled on this account'
                  : 'For a marker the lab reports in words — a urinalysis color, “Negative”, “Not detected”'
              }
              onClick={() =>
                this.addRow(group.value, CustomMarkerResultType.TEXT)
              }
            >
              {locked ? <FontAwesomeIcon icon="lock" /> : null}
              {locked ? ' ' : null}+ Add a text marker
            </Button>
            {this.renderCalculatedNote()}
          </div>
        </div>
      );
    }

    return (
      <div>
        {/* Deliberately one short line. The rest (where they show up in the
            analysis, that the RESULT stays with this test, what each Chart Type
            does, how the ranges are derived) lives in the ⓘ popover on the
            card title, because it is read once and then never again.
            This used to read "saved with this test only", which is true of the
            result and false of the marker: every save hands
            learnDefinitionsFromMarkers any name the library does not have yet,
            so the line was contradicting the popover it points at and burying
            the one fact that stops the practitioner defining the same marker
            again on the next client. */}
        <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px' }}>
          For markers on the lab report that LabSmarts does not cover yet.
          Defined once, then kept in <strong>My Markers</strong> for next time.
          See the{' '}
          <FontAwesomeIcon icon="info-circle" style={{ color: '#4a76a8' }} /> in
          the title above for how this works.
        </p>

        {carryForwardNote}

        {hasLibrary ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
              marginBottom: '10px',
            }}
          >
            <MarkerPicker
              library={library || []}
              sections={this.props.sections}
              inUse={inUse}
              locked={locked}
              onAdd={this.addDefinitions}
            />
            <span style={{ fontSize: '12px', color: '#6c757d' }}>
              {/* Opens in a NEW TAB on purpose. Nothing on this form is
                  autosaved, so navigating away mid-entry throws away whatever
                  has been typed into the test — a link that costs the
                  practitioner their work is worse than no link. Kept as a plain
                  anchor rather than a react-router Link so this table still
                  renders outside a Router. */}
              Sections and ranges come from{' '}
              <a href="/my-markers" target="_blank" rel="noopener noreferrer">
                <strong>My Markers</strong>
              </a>{' '}
              (under <strong>Test Results</strong> in the menu), matched to this
              client&rsquo;s sex where you set one.
            </span>
          </div>
        ) : null}

        {/* No table at all once every marker has moved to a section card of
            its own: a column header with nothing under it is a table that
            looks broken. The picker and the add button below still stand,
            which is what this card is for at that point. */}
        {shown.length === 0 ? null : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
            {this.renderHead(
              shown.every(group =>
                group.items.every(({ row, index }) =>
                  this.isCompact(row, index),
                ),
              ),
              appendixDragColumn,
            )}
            <tbody>
              {shown.map(group => {
                const groupKey = sectionGroupKey(group.value);
                // Only foldable while there is a heading to unfold it with.
                // Deleting the other sections drops `grouped` back to false, and
                // a stale fold flag would then hide every row on the test with
                // no control left to bring them back.
                const folded =
                  groupedFor(shown) && !!this.state.collapsedGroups[groupKey];
                return (
                  <React.Fragment key={groupKey}>
                    {groupedFor(shown)
                      ? this.renderGroupHeading(group, groupKey, folded)
                      : null}
                    {folded
                      ? null
                      : group.items.map(({ row, index }, _i, items) =>
                          this.isCompact(row, index)
                            ? this.renderCompactRow(
                                row,
                                index,
                                items.map(i => i.index),
                                appendixDragColumn,
                              )
                            : this.renderFullRow(
                                row,
                                index,
                                items.map(i => i.index),
                                appendixDragColumn,
                              ),
                        )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '8px',
          }}
        >
          {/* When the feature is off this stays CLICKABLE while looking
              locked — a truly disabled button swallows the click, and the
              click is what explains why it is unavailable. */}
          <Button
            variant={locked ? 'outline-secondary' : 'outline-primary'}
            size="sm"
            onClick={() => this.addRow()}
            title={locked ? 'Custom markers are not enabled on this account' : undefined}
            // The note beside this used to be long enough to squeeze the
            // button until its label broke onto two lines. The note is now
            // one sentence, and the button refuses to shrink or wrap either
            // way, so its label always sits on one line.
            style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
          >
            {locked ? <FontAwesomeIcon icon="lock" /> : null}
            {locked ? ' ' : null}+ Add another numeric marker
          </Button>
          {/* A button of its own rather than leaving it to be found inside the
              Chart Type dropdown. A practitioner entering a urinalysis is not
              looking for a chart setting — they are looking for somewhere to
              type words — and a control nobody opens the dropdown to find is a
              feature nobody has. */}
          <Button
            variant={locked ? 'outline-secondary' : 'outline-primary'}
            size="sm"
            onClick={() => this.addRow(undefined, CustomMarkerResultType.TEXT)}
            title={
              locked
                ? 'Custom markers are not enabled on this account'
                : 'For a marker the lab reports in words — a urinalysis color, “Negative”, “Not detected”'
            }
            style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
          >
            {locked ? <FontAwesomeIcon icon="lock" /> : null}
            {locked ? ' ' : null}+ Add a text marker
          </Button>
          {/* Which fields are required is carried by the red asterisks in the
              column headers, and the long form of all of this is in the ⓘ
              popover on the title. */}
          {this.renderCalculatedNote()}
        </div>
      </div>
    );
  }
}
